import { create } from 'zustand';
import { WorkData, WorkSortData, WorkFilterData } from '../dto/EntityRepository';
import { SortDirection, WorkType, WorkSortOrder, PerPage, perPageToNumber, WorkListType } from '../enum/EnumRepository';
import type { ResourceScope, ResourceStatus } from '../util/scopedResource';


interface WorkListState {
  pageSize: PerPage;
  setPageSize: (pageSize: PerPage) => void;
  sortData: WorkSortData;
  setSortData: (sortData: WorkSortData) => void;
  filterData: WorkFilterData;
  setFilterData: (filterData: WorkFilterData) => void;
  clearCreditFilter: () => void;
  resetListQuery: () => void;
  workListType: WorkListType;
  setWorkListType: (workListType: WorkListType) => void;
  workList: WorkData[];
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
  setWorkList: (workList: WorkData[]) => void;
  beginWorkListFetch: (ownerAui: string, scope: ResourceScope) => void;
  setWorkListWithScope: (
    workList: WorkData[],
    ownerAui: string,
    scope: ResourceScope,
  ) => void;
  setWorkListError: () => void;
  addWork: (work: WorkData, pageSize: PerPage) => void;
  addWorkList: (workList: WorkData[]) => void;
  updateAfterIsPublicToggle: (workId: string, isPublic: boolean) => void;
  clearWorkList: () => void;
}

const isSameWork = (prev: WorkData, next: WorkData): boolean =>
  prev.id === next.id &&
  prev.title === next.title &&
  prev.titleEn === next.titleEn &&
  prev.workType === next.workType &&
  prev.prodYear === next.prodYear &&
  prev.material === next.material &&
  prev.isPOV === next.isPOV &&
  prev.isPublic === next.isPublic &&
  prev.uploadFile?.originUrl === next.uploadFile?.originUrl &&
  prev.artist?.name === next.artist?.name &&
  prev.artist?.nameEn === next.artist?.nameEn &&
  prev.artist?.linkedMemberAui === next.artist?.linkedMemberAui &&
  prev.artist?.linkedMemberHandle === next.artist?.linkedMemberHandle;

const isSameWorkList = (prev: WorkData[], next: WorkData[]): boolean =>
  prev.length === next.length &&
  prev.every((work, index) => isSameWork(work, next[index]));

/** 본문 Works 목록과 분리된 모달(import/export) 조회용 기본 필터 */
export const getDefaultWorkFilterData = (): WorkFilterData => ({
  workType: {
    [WorkType.PAINTING]: true,
    [WorkType.DRAWING]: true,
    [WorkType.PRINTS]: true,
    [WorkType.PHOTO]: true,
    [WorkType.DIGITAL]: true,
    [WorkType.INSTALLATION]: true,
    [WorkType.SCULPTURE]: true,
    [WorkType.CERAMIC]: true,
    [WorkType.TEXTILE]: true,
    [WorkType.CONCEPTUAL]: true,
    [WorkType.VIDEO]: true,
    [WorkType.PERFORMANCE]: true,
    [WorkType.BOOK]: true,
    [WorkType.NONE]: true,
  },
  prodYear: { min: null, max: null },
  tagIds: [],
  creditIds: [],
});

/** 본문 Works 첫 랜딩: index 지정 Work 우선, 나머지는 최신 연도순 */
export const getDefaultWorkSortData = (): WorkSortData => ({
  sort: WorkSortOrder.YEAR,
  direction: SortDirection.DESC,
  manualOrderFirst: true,
});

/** import/export 모달 기본 정렬 */
export const getDefaultModalWorkSortData = (): WorkSortData => ({
  sort: WorkSortOrder.MANUAL_ORDER,
  direction: SortDirection.ASC,
});

export const useWorkListStore = create<WorkListState>((set, get) => ({
  pageSize: PerPage.PAGE_10,
  setPageSize: (pageSize) => set({ pageSize }),
  sortData: getDefaultWorkSortData(),
  setSortData: (sortData) => set({ sortData }),
  filterData: getDefaultWorkFilterData(),
  setFilterData: (filterData) => set({ filterData }),
  clearCreditFilter: () =>
    set(({ filterData }) => ({
      filterData: { ...filterData, creditIds: [] },
    })),
  resetListQuery: () =>
    set({
      pageSize: PerPage.PAGE_10,
      sortData: getDefaultWorkSortData(),
      filterData: getDefaultWorkFilterData(),
    }),
  workListType: WorkListType.GRID,
  setWorkListType: (workListType) => set({ workListType }),
  workList: [],
  listOwnerAui: null,
  listScope: null,
  status: 'idle',
  setWorkList: (workList) => set({ workList }),
  beginWorkListFetch: (ownerAui, scope) => set((state) => {
    // 같은 owner+scope 재fetch: success 유지(SWR). 다르면 loading으로 숨김.
    if (
      state.listOwnerAui === ownerAui &&
      state.listScope === scope &&
      state.status === 'success'
    ) {
      return state;
    }
    return { status: 'loading' };
  }),
  setWorkListWithScope: (workList, ownerAui, scope) =>
    set((state) => {
      if (
        state.status === 'success' &&
        state.listOwnerAui === ownerAui &&
        state.listScope === scope &&
        isSameWorkList(state.workList, workList)
      ) {
        return state;
      }
      return {
        workList,
        listOwnerAui: ownerAui,
        listScope: scope,
        status: 'success',
      };
    }),
  setWorkListError: () => set({ status: 'error' }),
  addWork: (work, pageSize: PerPage) => {
    const currentList = get().workList;
    const pageSizeNumber = perPageToNumber(pageSize);
    const newList = [work, ...currentList].slice(0, pageSizeNumber);
    set({ workList: newList });
  },
  addWorkList: (workList) => {
    const currentList = get().workList;
    const newList = [...currentList, ...workList];
    set({ workList: newList });
  },
  updateAfterIsPublicToggle: (workId, isPublic) => {
    const currentList = get().workList;
    const newList = currentList.map((work) =>
      work.id === workId ? { ...work, isPublic } : work
    );
    set({ workList: newList });
  },
  clearWorkList: () => set((state) => {
    if (
      state.workList.length === 0 &&
      state.listOwnerAui === null &&
      state.listScope === null &&
      state.status === 'idle'
    ) {
      return state;
    }
    return {
      workList: [],
      listOwnerAui: null,
      listScope: null,
      status: 'idle',
    };
  }),
}));


interface BulkState {
  current: number;
  addCurrent: () => void;
  clearCurrent: () => void;
  total: number;
  setTotal: (total: number) => void;  
}

export const useBulkStore = create<BulkState>((set, get) => ({
  current: 0,
  addCurrent: () => set({ current: get().current + 1 }),
  clearCurrent: () => set({ current: 0 }),
  total: 0,
  setTotal: (total) => set({ total }),
}));
